/**
 * MONSTER TOWN — WHAT THE DAY LOOKS LIKE. TIME-OF-DAY CREW (src/time/**).
 *
 * THE TABLE IS THE FEATURE. Everything the cycle does to the picture — the sky
 * gradient, the colour and angle of the sun, the ambient bounce, how much of
 * the "night layer" (stars, lit windows, glowing bulbs) is showing — is one
 * keyframe table read at a day fraction. Re-tuning the mood of six o'clock is a
 * number in this file, never a change in an engine.
 *
 * RULES THIS FILE OBEYS
 *  - EVERY COLOUR COMES OUT OF THE COMMITTED PALETTE (`src/core/palette.ts`).
 *    There is not one hex literal below. A band is a mood change inside the
 *    palette family, never a different game (expansion brief §1).
 *  - MIDDAY IS BIT-IDENTICAL TO THE SHIPPED GAME. The `day` keyframe carries the
 *    exact values `src/world/town.ts` and `src/render/renderer.ts` already use
 *    (skyTop / skyHorizon gradient, `setAmbience(skyHorizon, sand, 1.05)`, a
 *    1.5-intensity `sunWarm` sun), so a kid who plays at noon sees the game they
 *    already have.
 *  - NIGHT IS COSY, NOT DARK. This is for a three-year-old. Deepest night still
 *    carries a lilac horizon glow, a moonlit hemisphere, and a warm unshadowed
 *    fill that lifts every shadow side by a hair — which is the whole difference
 *    between "evening" and "scary". NOTHING in the table ever reaches black: the
 *    darkest colour used anywhere is the palette's own `ink`, mixed no further
 *    than 55%, and `npm run time-check` measures the darkest sky in the entire
 *    day and fails if its brightest channel drops under 70/255.
 *  - PURE. `lookAt(t)` is a function of the day fraction and nothing else, so a
 *    tool can ask what 3am looks like without moving the clock.
 */

import { hex } from '../core/palette'

// ---------------------------------------------------------------------------
// colour helpers (numbers, because a look is interpolated far too often to be
// doing string work — `mix()` in the palette only blends two NAMES)
// ---------------------------------------------------------------------------

export function blendHex(a: number, b: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t
  const r = Math.round(((a >> 16) & 255) * (1 - k) + ((b >> 16) & 255) * k)
  const g = Math.round(((a >> 8) & 255) * (1 - k) + ((b >> 8) & 255) * k)
  const bl = Math.round((a & 255) * (1 - k) + (b & 255) * k)
  return (r << 16) | (g << 8) | bl
}

export function cssHex(n: number): string {
  return `#${(n & 0xffffff).toString(16).padStart(6, '0')}`
}

const P = {
  skyTop: hex('skyTop'),
  skyHorizon: hex('skyHorizon'),
  sunWarm: hex('sunWarm'),
  ambientCool: hex('ambientCool'),
  shadow: hex('shadow'),
  ink: hex('ink'),
  sand: hex('sand'),
  lilac: hex('monsterLilac'),
  sky: hex('monsterSky'),
  pink: hex('flowerPink'),
  mustard: hex('roofMustard'),
  coin: hex('uiCoin'),
  cream: hex('flowerWhite'),
} as const

/** The shipped mid-gradient stop — `PALE_SKY` in src/world/terrain.ts. */
const DAY_MID = blendHex(P.skyTop, P.skyHorizon, 0.5)

// ---------------------------------------------------------------------------
// the grade palette — five moods, all mixed out of the committed palette
// ---------------------------------------------------------------------------

/**
 * Deep night. Blue-violet, NOT grey: it must cool the town, not drain it.
 *
 * THE LAST 14% IS THE DIFFERENCE BETWEEN CREAM AND CONCRETE. A multiply scales
 * the three channels of whatever is underneath, so how much warmth survives the
 * night is exactly the tint's own blue-over-red ratio. At a straight
 * shadow/lilac mix that ratio is 1.40 and the plaza came out #575250 — warm by
 * a hair (r-b 7) where the critic had measured a hue-inverted #5e5d5c, but
 * still nearly drained. Taking it a seventh of the way back toward `sunWarm`
 * puts the ratio at 1.26 and leaves the cream reading as cream, while the
 * violet is still plainly the colour of the night.
 */
const G_NIGHT = blendHex(blendHex(P.shadow, P.lilac, 0.38), P.sunWarm, 0.14)
/**
 * The warm lift that stops a multiply going muddy in the shadow side — and it
 * has to be genuinely WARM to do that job. A lilac-leaning lift is very nearly
 * neutral, so it lightened the night without putting anything back: measured,
 * the road came out #4c4c50, a hair BLUER than neutral, from a noon that was
 * warm by 28. An amber lift adds more red than blue to every surface at once,
 * which is what a town lit by its own windows and lamps actually looks like.
 */
const G_NIGHT_GLOW = blendHex(P.sunWarm, P.coin, 0.3)
/** Dusk: the violet arriving while the last gold is still on the rooftops. */
const G_DUSK = blendHex(P.lilac, P.coin, 0.44)
const G_DUSK_GLOW = blendHex(P.coin, P.pink, 0.35)
/** Gold evening. */
const G_GOLD = blendHex(P.coin, P.mustard, 0.35)
const G_GOLD_GLOW = P.coin
/**
 * ============ SUNRISE, AND WHY IT WAS BUBBLEGUM ============
 *
 * Critic round 2: *"Dawn is not a warm sunrise — it is a bubblegum-pink grade
 * over unchanged green grass. Every paved surface, wall, roof, kerb and even the
 * fountain basin is a uniform dusty salmon-pink, while the grass and tree
 * canopies stay flat mid-green."* (`shots/critic-vr2/wide-0500.png`.) True, and
 * the arithmetic says exactly why.
 *
 * A MULTIPLY MAKES WARMTH BY TAKING BLUE AWAY, NOT BY TAKING GREEN AWAY.
 * The old tint was `blend(flowerPink, roofMustard, 0.3)` = (250,154,143), and
 * `flowerPink` is a colour whose GREEN channel (143) is BELOW its BLUE (177). So
 * the tint's channel order was R > B > G — magenta-leaning — and a multiply by
 * it does two bad things at once:
 *   - on a NEUTRAL surface (paving, cream wall, kerb, roof) it stamps its own
 *     hue straight on: salmon;
 *   - on a SATURATED GREEN it can only remove the one channel the grass is
 *     made of, so the lawn just goes darker green and never warms at all.
 * The picture therefore splits into pink hardscape on cold green, which is not a
 * palette family — it is what a colour-cast bug looks like.
 *
 * The gold hour is the proof: `G_GOLD` = (249,189,62) is R > G > B, so it warms
 * the grass (r-b 41 -> 51) at the same time as the paving, and `wide-1800.png`
 * genuinely works. The dawn tint is now built the same way round — a PEACH, with
 * the pink in the minority so the blue drops below the green — and it is
 * measurably gentler than the gold hour, because a sunrise is.
 */
const G_DAWN = blendHex(P.mustard, P.pink, 0.42)
/**
 * The additive lift is the other half, and it was pink too: the old
 * `blend(flowerPink, sunWarm, 0.42)` = (255,185,193), again B > G, so it ADDED
 * more blue than green to every surface in the town. An additive lift is the
 * only part of the grade that can warm a saturated green (it adds absolute R),
 * so this one is a rosy amber and it does a bit more of the work than it used
 * to — see `gradeGlowAmount` in the dawn keyframes.
 */
const G_DAWN_GLOW = blendHex(blendHex(P.sunWarm, P.coin, 0.45), P.pink, 0.18)
/** The whisper of warmth still on seven o'clock. */
const G_MORNING = blendHex(P.sunWarm, P.coin, 0.35)
const G_MORNING_GLOW = P.sunWarm

// ---------------------------------------------------------------------------
// the look
// ---------------------------------------------------------------------------

export interface DayLook {
  /** Sky gradient, top -> middle -> horizon. Painted into the backdrop. */
  readonly skyTop: number
  readonly skyMid: number
  readonly skyHorizon: number
  readonly fog: number
  /** The key light. */
  readonly sunColor: number
  readonly sunIntensity: number
  /** Radians around Y. 0 = the shipped noon direction; +/- swings east/west. */
  readonly sunAzimuth: number
  /** How high the sun rides. Low = long storybook shadows. */
  readonly sunHeight: number
  /** The hemisphere bounce — `renderer.setAmbience`. */
  readonly hemiSky: number
  readonly hemiGround: number
  readonly hemiIntensity: number
  /** An extra unshadowed warm fill so nothing ever goes muddy after dark. */
  readonly fillColor: number
  readonly fillIntensity: number
  /** Multiplier on lights other crews parented into a scene (the town's fill). */
  readonly sceneLightScale: number
  /** 0..1. Stars out, bulbs glowing, windows warm. THE night layer. */
  readonly night: number

  /**
   * ---- THE COLOUR GRADE, and why the lighting alone could not do this -------
   *
   * MEASURED, not assumed. `tools/critic-vr1-ui.mjs` probed the canvas at five
   * fixed points at thirteen hours and found 07:00, 09:00 and 12:00
   * BIT-IDENTICAL at every one of them, and 15:00 within one step. The cause is
   * `MeshToonMaterial`: every surface in this game quantises its diffuse term
   * through a NearestFilter gradient map, so a sun that is 5% warmer and 5%
   * weaker lands in exactly the same band and changes not one pixel. Two thirds
   * of the day was therefore one static look, and the brief's "warm sunrise ->
   * bright midday -> gold evening" lived only in the sky gradient — which the
   * world crew's tree line hides at every zoom step.
   *
   * So the warm hours are graded ON THE FINISHED PICTURE, the same trick
   * `tint.ts` already proved for night: one MULTIPLY pane (hue and darkness)
   * and one ADDITIVE pane (the warm lift that stops multiply going muddy).
   * A multiply keeps the palette family by construction — it scales the three
   * channels of whatever is underneath rather than mixing a foreign colour into
   * it, so a cream wall stays a cream wall and never becomes grey concrete.
   *
   * MIDDAY IS STILL EXACTLY THE SHIPPED GAME: `gradeStrength` and
   * `gradeGlowAmount` are BOTH ZERO from 09:00 to 15:00, and at zero neither
   * pane is drawn at all.
   */
  /** The multiply colour. Palette-derived, never a hex literal. */
  readonly gradeTint: number
  /** 0..1 — how far the multiply goes from neutral. 0 = the pane is not drawn. */
  readonly gradeStrength: number
  /** The additive lift's colour. */
  readonly gradeGlow: number
  /** 0..1 — how much of it. 0 = the pane is not drawn. */
  readonly gradeGlowAmount: number
}

interface LookKey extends DayLook {
  /** Day fraction 0..1 this keyframe sits at. */
  readonly at: number
}

const NOON_SUN_DIST = Math.hypot(14, 10)

/**
 * THE TABLE. Hours in the comments; `at` is the day fraction the clock speaks.
 *
 * Read it top to bottom and you have the brief's sentence: warm sunrise ->
 * bright midday -> gold evening -> soft night.
 */
const KEYS: readonly LookKey[] = [
  // ---- 00:00 deep night ---------------------------------------------------
  {
    at: 0,
    skyTop: blendHex(P.shadow, P.ink, 0.55),
    skyMid: blendHex(P.shadow, P.lilac, 0.22),
    skyHorizon: blendHex(P.shadow, P.lilac, 0.55),
    fog: blendHex(P.shadow, P.lilac, 0.45),
    sunColor: blendHex(P.ambientCool, P.sky, 0.35),
    sunIntensity: 0.13,
    sunAzimuth: -0.5,
    sunHeight: 22,
    /**
     * THE GROUND BOUNCE STAYS WARM AFTER DARK, and that is the whole fix for
     * "cream houses turn into cold blue-grey concrete". A toon surface is its
     * own albedo multiplied by the light on it: light everything with cool
     * blue-grey and a warm sand pavement comes out neutral grey — measured,
     * plaza #dfcb98 -> #5e5d5c. The cool now lives in the MULTIPLY grade below
     * (which scales channels and so cannot pull a hue out), and the light
     * itself keeps a warm bounce and a real warm fill.
     */
    hemiSky: blendHex(P.ambientCool, P.shadow, 0.62),
    hemiGround: blendHex(P.sand, P.shadow, 0.5),
    hemiIntensity: 0.42,
    fillColor: blendHex(P.sunWarm, P.mustard, 0.22),
    fillIntensity: 0.26,
    sceneLightScale: 0.1,
    night: 1,
    gradeTint: G_NIGHT,
    gradeStrength: 0.62,
    gradeGlow: G_NIGHT_GLOW,
    gradeGlowAmount: 0.085,
  },
  // ---- 04:20 still night, the horizon starts to think about it ------------
  {
    at: 0.18,
    skyTop: blendHex(P.shadow, P.ink, 0.5),
    skyMid: blendHex(P.shadow, P.lilac, 0.26),
    skyHorizon: blendHex(P.shadow, P.lilac, 0.62),
    fog: blendHex(P.shadow, P.lilac, 0.5),
    sunColor: blendHex(P.ambientCool, P.sky, 0.35),
    sunIntensity: 0.14,
    sunAzimuth: -0.62,
    sunHeight: 21,
    hemiSky: blendHex(P.ambientCool, P.shadow, 0.6),
    hemiGround: blendHex(P.sand, P.shadow, 0.48),
    hemiIntensity: 0.44,
    fillColor: blendHex(P.sunWarm, P.mustard, 0.22),
    fillIntensity: 0.26,
    sceneLightScale: 0.11,
    night: 1,
    gradeTint: G_NIGHT,
    gradeStrength: 0.6,
    gradeGlow: G_NIGHT_GLOW,
    gradeGlowAmount: 0.085,
  },
  // ---- 05:00 first light --------------------------------------------------
  {
    at: 5 / 24,
    skyTop: blendHex(blendHex(P.shadow, P.lilac, 0.55), P.skyTop, 0.3),
    skyMid: blendHex(P.lilac, P.pink, 0.45),
    skyHorizon: blendHex(P.pink, P.mustard, 0.3),
    fog: blendHex(P.pink, P.sunWarm, 0.4),
    sunColor: blendHex(P.sunWarm, P.pink, 0.3),
    sunIntensity: 0.5,
    sunAzimuth: -0.78,
    sunHeight: 15,
    /**
     * THE BOUNCE IS WARM, NOT PINK — same reason as `G_DAWN` above. A hemisphere
     * whose sky colour has more BLUE than GREEN puts a cold cast on every
     * up-facing surface in the town, which is the other half of "pink hardscape
     * on cold green". Every colour here is R > G > B now.
     */
    hemiSky: blendHex(blendHex(P.skyHorizon, P.sunWarm, 0.4), blendHex(P.mustard, P.pink, 0.4), 0.24),
    hemiGround: blendHex(P.sand, P.pink, 0.22),
    hemiIntensity: 0.7,
    fillColor: blendHex(P.sunWarm, P.pink, 0.24),
    fillIntensity: 0.16,
    sceneLightScale: 0.4,
    night: 0.55,
    gradeTint: blendHex(G_NIGHT, G_DAWN, 0.55),
    // Less of the multiply (which can only ever DARKEN a green) and more of the
    // additive lift (which is the only part that can warm one). See G_DAWN_GLOW.
    gradeStrength: 0.36,
    gradeGlow: G_DAWN_GLOW,
    gradeGlowAmount: 0.13,
  },
  // ---- 05:55 SUNRISE, the warmest minute of the morning -------------------
  {
    at: 0.246,
    skyTop: blendHex(P.skyTop, P.lilac, 0.4),
    skyMid: blendHex(P.pink, P.sunWarm, 0.4),
    skyHorizon: blendHex(P.sunWarm, P.pink, 0.32),
    fog: blendHex(P.sunWarm, P.pink, 0.28),
    sunColor: blendHex(P.sunWarm, P.pink, 0.22),
    sunIntensity: 1.05,
    sunAzimuth: -0.72,
    sunHeight: 16,
    hemiSky: blendHex(blendHex(P.skyHorizon, P.sunWarm, 0.45), blendHex(P.mustard, P.pink, 0.4), 0.2),
    hemiGround: blendHex(P.sand, P.pink, 0.15),
    hemiIntensity: 0.9,
    fillColor: blendHex(P.sunWarm, P.pink, 0.16),
    fillIntensity: 0.06,
    sceneLightScale: 0.8,
    night: 0.18,
    gradeTint: G_DAWN,
    gradeStrength: 0.32,
    gradeGlow: G_DAWN_GLOW,
    // Measured: at 0.17 the whole 06:00 frame came out at mean luminance 155.7
    // against noon's 153.2 — a SUNRISE BRIGHTER THAN MIDDAY, which is the same
    // class of mistake as the night that measured brighter than noon in round 1.
    gradeGlowAmount: 0.14,
  },
  // ---- 07:00 THE HOUR THE BED LANDS ON, and a fresh town's first frame.
  //      Bright, with a whisper of morning warmth still on it.
  {
    at: 7 / 24,
    skyTop: blendHex(P.skyTop, P.sunWarm, 0.1),
    skyMid: blendHex(DAY_MID, P.sunWarm, 0.14),
    skyHorizon: blendHex(P.skyHorizon, P.sunWarm, 0.2),
    fog: blendHex(P.skyHorizon, P.sunWarm, 0.16),
    sunColor: blendHex(P.sunWarm, P.pink, 0.08),
    sunIntensity: 1.42,
    sunAzimuth: -0.42,
    sunHeight: 20,
    hemiSky: blendHex(P.skyHorizon, P.sunWarm, 0.14),
    hemiGround: P.sand,
    hemiIntensity: 1.02,
    fillColor: P.sunWarm,
    fillIntensity: 0.03,
    sceneLightScale: 0.96,
    night: 0,
    gradeTint: G_MORNING,
    gradeStrength: 0.14,
    gradeGlow: G_MORNING_GLOW,
    gradeGlowAmount: 0.05,
  },
  // ---- 09:00 broad daylight. EXACTLY THE SHIPPED GAME. --------------------
  {
    at: 9 / 24,
    skyTop: P.skyTop,
    skyMid: DAY_MID,
    skyHorizon: P.skyHorizon,
    fog: P.skyHorizon,
    sunColor: P.sunWarm,
    sunIntensity: 1.5,
    sunAzimuth: 0,
    sunHeight: 24,
    hemiSky: P.skyHorizon,
    hemiGround: P.sand,
    hemiIntensity: 1.05,
    fillColor: P.sunWarm,
    fillIntensity: 0,
    sceneLightScale: 1,
    night: 0,
    // BOTH ZERO. Neither grade pane is drawn between 09:00 and 15:00, so broad
    // daylight is bit-identical to the shipped game. `time-check` asserts it.
    gradeTint: G_MORNING,
    gradeStrength: 0,
    gradeGlow: G_MORNING_GLOW,
    gradeGlowAmount: 0,
  },
  // ---- 15:00 still broad daylight, sun swinging over ----------------------
  {
    at: 15 / 24,
    skyTop: P.skyTop,
    skyMid: DAY_MID,
    skyHorizon: P.skyHorizon,
    fog: P.skyHorizon,
    sunColor: P.sunWarm,
    sunIntensity: 1.5,
    sunAzimuth: 0.34,
    sunHeight: 23,
    hemiSky: P.skyHorizon,
    hemiGround: P.sand,
    hemiIntensity: 1.05,
    fillColor: P.sunWarm,
    fillIntensity: 0,
    sceneLightScale: 1,
    night: 0,
    gradeTint: G_GOLD,
    gradeStrength: 0,
    gradeGlow: G_GOLD_GLOW,
    gradeGlowAmount: 0,
  },
  // ---- 17:00 the gold starts ---------------------------------------------
  {
    at: 17 / 24,
    skyTop: blendHex(P.skyTop, P.lilac, 0.3),
    skyMid: blendHex(DAY_MID, P.coin, 0.3),
    skyHorizon: blendHex(P.coin, P.sunWarm, 0.4),
    fog: blendHex(P.coin, P.sunWarm, 0.42),
    sunColor: blendHex(P.sunWarm, P.mustard, 0.35),
    sunIntensity: 1.4,
    sunAzimuth: 0.55,
    sunHeight: 18,
    hemiSky: blendHex(P.skyHorizon, P.coin, 0.28),
    hemiGround: blendHex(P.sand, P.mustard, 0.25),
    hemiIntensity: 1.02,
    fillColor: blendHex(P.sunWarm, P.mustard, 0.3),
    fillIntensity: 0.04,
    sceneLightScale: 1,
    night: 0,
    gradeTint: G_GOLD,
    gradeStrength: 0.24,
    gradeGlow: G_GOLD_GLOW,
    gradeGlowAmount: 0.06,
  },
  // ---- 18:20 GOLD EVENING, long shadows across the street -----------------
  {
    at: 0.764,
    skyTop: blendHex(blendHex(P.skyTop, P.lilac, 0.55), P.shadow, 0.2),
    skyMid: blendHex(P.mustard, P.pink, 0.35),
    skyHorizon: blendHex(P.coin, P.sunWarm, 0.3),
    fog: blendHex(P.coin, P.pink, 0.3),
    sunColor: blendHex(P.sunWarm, P.mustard, 0.55),
    sunIntensity: 1.2,
    sunAzimuth: 0.72,
    sunHeight: 13,
    hemiSky: blendHex(P.skyHorizon, P.mustard, 0.35),
    hemiGround: blendHex(P.sand, P.mustard, 0.4),
    hemiIntensity: 0.95,
    fillColor: blendHex(P.sunWarm, P.mustard, 0.4),
    fillIntensity: 0.06,
    sceneLightScale: 0.95,
    night: 0.08,
    gradeTint: G_GOLD,
    gradeStrength: 0.4,
    gradeGlow: G_GOLD_GLOW,
    gradeGlowAmount: 0.1,
  },
  // ---- 19:20 dusk: violet above, the last gold on the rooftops ------------
  {
    at: 0.806,
    skyTop: blendHex(P.lilac, P.shadow, 0.5),
    skyMid: blendHex(P.lilac, P.mustard, 0.35),
    skyHorizon: blendHex(P.mustard, P.pink, 0.45),
    fog: blendHex(P.lilac, P.mustard, 0.3),
    sunColor: blendHex(P.mustard, P.lilac, 0.5),
    sunIntensity: 0.5,
    sunAzimuth: 0.84,
    sunHeight: 12,
    hemiSky: blendHex(P.ambientCool, P.lilac, 0.45),
    hemiGround: blendHex(P.sand, P.lilac, 0.42),
    hemiIntensity: 0.62,
    fillColor: blendHex(P.sunWarm, P.mustard, 0.3),
    fillIntensity: 0.16,
    sceneLightScale: 0.45,
    night: 0.5,
    gradeTint: G_DUSK,
    gradeStrength: 0.46,
    gradeGlow: G_DUSK_GLOW,
    gradeGlowAmount: 0.1,
  },
  // ---- 20:20 nightfall: the lamps and the windows are the show ------------
  {
    at: 0.847,
    skyTop: blendHex(P.shadow, P.ink, 0.45),
    skyMid: blendHex(P.shadow, P.lilac, 0.3),
    skyHorizon: blendHex(P.lilac, P.shadow, 0.45),
    fog: blendHex(P.shadow, P.lilac, 0.42),
    sunColor: blendHex(P.ambientCool, P.lilac, 0.3),
    sunIntensity: 0.2,
    sunAzimuth: 0.6,
    sunHeight: 18,
    hemiSky: blendHex(P.ambientCool, P.shadow, 0.58),
    hemiGround: blendHex(P.sand, P.shadow, 0.44),
    hemiIntensity: 0.5,
    fillColor: blendHex(P.sunWarm, P.mustard, 0.26),
    fillIntensity: 0.24,
    sceneLightScale: 0.18,
    night: 0.92,
    gradeTint: blendHex(G_DUSK, G_NIGHT, 0.7),
    gradeStrength: 0.58,
    gradeGlow: blendHex(G_DUSK_GLOW, G_NIGHT_GLOW, 0.6),
    gradeGlowAmount: 0.08,
  },
  // ---- 21:30 night proper. Same values as 00:00 so the small hours are flat.
  {
    at: 0.896,
    skyTop: blendHex(P.shadow, P.ink, 0.55),
    skyMid: blendHex(P.shadow, P.lilac, 0.22),
    skyHorizon: blendHex(P.shadow, P.lilac, 0.55),
    fog: blendHex(P.shadow, P.lilac, 0.45),
    sunColor: blendHex(P.ambientCool, P.sky, 0.35),
    sunIntensity: 0.32,
    sunAzimuth: 0.4,
    sunHeight: 22,
    hemiSky: blendHex(P.ambientCool, P.shadow, 0.6),
    hemiGround: blendHex(P.sand, P.shadow, 0.48),
    hemiIntensity: 0.5,
    fillColor: blendHex(P.sunWarm, P.mustard, 0.22),
    fillIntensity: 0.26,
    sceneLightScale: 0.14,
    night: 1,
    gradeTint: G_NIGHT,
    gradeStrength: 0.62,
    gradeGlow: G_NIGHT_GLOW,
    gradeGlowAmount: 0.085,
  },
]

const SORTED = [...KEYS].sort((a, b) => a.at - b.at)

const smooth = (k: number): number => k * k * (3 - 2 * k)

function lerpKey(a: LookKey, b: LookKey, k: number): DayLook {
  const s = smooth(k)
  const n = (x: number, y: number): number => x + (y - x) * s
  const c = (x: number, y: number): number => blendHex(x, y, s)
  return {
    skyTop: c(a.skyTop, b.skyTop),
    skyMid: c(a.skyMid, b.skyMid),
    skyHorizon: c(a.skyHorizon, b.skyHorizon),
    fog: c(a.fog, b.fog),
    sunColor: c(a.sunColor, b.sunColor),
    sunIntensity: n(a.sunIntensity, b.sunIntensity),
    sunAzimuth: n(a.sunAzimuth, b.sunAzimuth),
    sunHeight: n(a.sunHeight, b.sunHeight),
    hemiSky: c(a.hemiSky, b.hemiSky),
    hemiGround: c(a.hemiGround, b.hemiGround),
    hemiIntensity: n(a.hemiIntensity, b.hemiIntensity),
    fillColor: c(a.fillColor, b.fillColor),
    fillIntensity: n(a.fillIntensity, b.fillIntensity),
    sceneLightScale: n(a.sceneLightScale, b.sceneLightScale),
    night: n(a.night, b.night),
    gradeTint: c(a.gradeTint, b.gradeTint),
    gradeStrength: n(a.gradeStrength, b.gradeStrength),
    gradeGlow: c(a.gradeGlow, b.gradeGlow),
    gradeGlowAmount: n(a.gradeGlowAmount, b.gradeGlowAmount),
  }
}

/**
 * THE LOOK AT A DAY FRACTION. Pure, total, and wraps through midnight: `t` may
 * be any number and 0.999 blends into the first keyframe, not off a cliff.
 *
 * There is no popping anywhere in the cycle by construction — every value in
 * `DayLook` is smoothstep-interpolated between two keyframes, so the only way to
 * get a hard edge would be two keyframes at the same `at`.
 */
export function lookAt(t: number): DayLook {
  const f = ((t % 1) + 1) % 1
  const last = SORTED[SORTED.length - 1] as LookKey
  const first = SORTED[0] as LookKey
  let a = last
  let b = first
  let from = last.at - 1
  let to = first.at
  for (let i = 0; i < SORTED.length; i++) {
    const key = SORTED[i] as LookKey
    if (f >= key.at) {
      a = key
      from = key.at
      const next = SORTED[i + 1]
      if (next === undefined) {
        b = first
        to = first.at + 1
      } else {
        b = next
        to = next.at
      }
    }
  }
  const span = to - from
  return lerpKey(a, b, span <= 0 ? 0 : (f - from) / span)
}

/** Blend a look toward another one. Used for interiors, and by the bed fade. */
export function blendLook(a: DayLook, b: DayLook, k: number): DayLook {
  const t = k < 0 ? 0 : k > 1 ? 1 : k
  const n = (x: number, y: number): number => x + (y - x) * t
  const c = (x: number, y: number): number => blendHex(x, y, t)
  return {
    skyTop: c(a.skyTop, b.skyTop),
    skyMid: c(a.skyMid, b.skyMid),
    skyHorizon: c(a.skyHorizon, b.skyHorizon),
    fog: c(a.fog, b.fog),
    sunColor: c(a.sunColor, b.sunColor),
    sunIntensity: n(a.sunIntensity, b.sunIntensity),
    sunAzimuth: n(a.sunAzimuth, b.sunAzimuth),
    sunHeight: n(a.sunHeight, b.sunHeight),
    hemiSky: c(a.hemiSky, b.hemiSky),
    hemiGround: c(a.hemiGround, b.hemiGround),
    hemiIntensity: n(a.hemiIntensity, b.hemiIntensity),
    fillColor: c(a.fillColor, b.fillColor),
    fillIntensity: n(a.fillIntensity, b.fillIntensity),
    sceneLightScale: n(a.sceneLightScale, b.sceneLightScale),
    night: n(a.night, b.night),
    gradeTint: c(a.gradeTint, b.gradeTint),
    gradeStrength: n(a.gradeStrength, b.gradeStrength),
    gradeGlow: c(a.gradeGlow, b.gradeGlow),
    gradeGlowAmount: n(a.gradeGlowAmount, b.gradeGlowAmount),
  }
}

/** Noon, for anything that needs "the shipped game" as a reference. */
export const DAY_LOOK: DayLook = lookAt(9 / 24)

/** The sun's world offset from whatever it is aimed at, for this look. */
export function sunOffsetFor(look: DayLook): { x: number; y: number; z: number } {
  // The shipped noon direction is (14, 24, 10); the azimuth rotates that vector
  // around Y so morning and evening throw their shadows the other way.
  const base = Math.atan2(10, 14)
  const a = base + look.sunAzimuth
  return {
    x: Math.cos(a) * NOON_SUN_DIST,
    y: look.sunHeight,
    z: Math.sin(a) * NOON_SUN_DIST,
  }
}
